<template>
	<div class="w-full text-ihh-midnight-gray">
		<div class="mx-auto w-full max-w-[1160px] px-8 pb-24 pt-16">
			<template v-if="step === 'lookup'">
				<div class="mx-auto flex w-full max-w-[34rem] flex-col gap-12">
					<header class="flex flex-col gap-3 text-center">
						<h1 class="text-[44px] font-semibold leading-[1.1] tracking-[-0.02em]">Returns</h1>
						<p class="text-[19px] leading-normal text-[#5E5863]">Sometimes things don't go as planned, and we understand.</p>
					</header>
					<section class="flex flex-col gap-4">
						<h2 class="text-[22px] font-semibold tracking-[-0.01em]">Return policy</h2>
						<ul class="m-0 flex list-none flex-col gap-3 p-0">
							<li v-for="rule in policyRules" :key="rule" class="flex items-start gap-3 text-base leading-snug">
								<span class="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#C9665A]" aria-hidden="true"></span>
								<span>{{ rule }}</span>
							</li>
						</ul>
					</section>
					<section class="flex flex-col gap-4">
						<div class="flex flex-col gap-1.5">
							<h2 class="text-[22px] font-semibold tracking-[-0.01em]">Shipping your return</h2>
							<p class="text-base leading-normal text-[#5E5863]">You'll choose one of these after you look up your order. Both start with the search below.</p>
						</div>
						<div class="flex flex-col gap-3" role="list">
							<div role="listitem" class="flex flex-col gap-2 rounded-xl border border-[#E4DED8] bg-[#F8F5F2] px-5 py-4">
								<h3 class="flex items-center gap-2.5 text-[17px] font-semibold leading-snug">
									<img src="/images/ups.png" alt="" class="h-8 w-auto shrink-0" />
									Prepaid UPS Ground label
								</h3>
								<p class="text-base leading-normal text-[#5E5863]">We'll provide a prepaid label, and its cost will be deducted from your refund.</p>
							</div>
							<div class="flex justify-center">
								<svg class="h-6 w-auto" viewBox="0 0 576 512" aria-hidden="true">
									<path fill="#453C50" d="M182.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-96 96c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L128 141.3V448c0 17.7 14.3 32 32 32s32-14.3 32-32V141.3l41.4 41.4c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-96-96zm352 333.3c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L448 370.7V64c0-17.7-14.3-32-32-32s-32 14.3-32 32V370.7l-41.4-41.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l96 96c12.5 12.5 32.8 12.5 45.3 0l96-96z" />
								</svg>
								<span class="sr-only">or</span>
							</div>
							<div role="listitem" class="flex flex-col gap-2 rounded-xl border border-[#E4DED8] bg-[#F8F5F2] px-5 py-4">
								<h3 class="flex items-center gap-2.5 text-[17px] font-semibold leading-snug">
									<svg class="h-8 w-8 shrink-0" viewBox="0 0 32 32" fill="none" aria-hidden="true">
										<path fill="#C4A36A" d="M16 4.2 27.2 9.6v12.8L16 27.8 4.8 22.4V9.6L16 4.2Z" />
										<path d="M5.4 10.1 16 15.4 26.6 10.1" stroke="white" stroke-width="1.4" stroke-linejoin="round" />
										<path d="M16 15.4 V26.8" stroke="white" stroke-width="1.4" stroke-linecap="round" />
									</svg>
									Your own label
								</h3>
								<p class="text-base leading-normal text-[#5E5863]">Ship the return with any carrier you like, at your own expense.</p>
							</div>
						</div>
					</section>
					<aside class="flex w-full flex-col gap-6 rounded-2xl border border-[#ECE7E2] bg-white p-7 shadow-[0_1px_2px_rgba(58,53,64,0.04),0_8px_24px_rgba(58,53,64,0.05)]">
						<div v-if="store.user?.id" class="flex flex-col gap-1.5 border-b border-[#ECE7E2] pb-6">
							<h2 class="text-lg font-semibold">Your recent orders</h2>
							<p v-if="recentLoading" class="text-[15px] leading-normal text-[#7A7480]">Loading your orders…</p>
							<p v-else-if="!recent.length" class="text-[15px] leading-normal text-[#7A7480]">No orders from the last 90 days. Look one up below.</p>
							<div v-else class="mt-2 flex flex-col gap-2">
								<div v-for="order in recent" :key="order.orderNumber" class="flex items-center gap-3 rounded-[10px] border border-[#ECE7E2] p-3">
									<div class="min-w-0 flex-1">
										<div class="text-[15px] font-semibold">#{{ order.orderNumber }}</div>
										<div class="text-sm text-[#7A7480]">{{ orderMeta(order) }}</div>
									</div>
									<button type="button" class="h-9 shrink-0 rounded-lg border border-ihh-dark-salmon bg-white px-3.5 text-sm font-semibold text-ihh-midnight-gray transition-colors duration-150 hover:bg-[#FBE9E5] disabled:cursor-not-allowed disabled:opacity-50" :disabled="loading" @click="selectRecent(order)">
										Start Return
									</button>
								</div>
							</div>
						</div>
						<form class="flex flex-col gap-[18px]" @submit.prevent="lookup">
							<label class="flex flex-col gap-2 text-[15px] font-medium">
								Order number
								<input v-model.trim="orderNumber" required type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" class="h-12 w-full rounded-[10px] border border-[#DDD6D0] bg-white px-3.5 font-normal text-ihh-midnight-gray outline-none focus:!border-[#C9665A] focus:!shadow-[0_0_0_3px_rgba(229,151,138,0.25)] focus:!ring-0" />
							</label>
							<label class="flex flex-col gap-2 text-[15px] font-medium">
								Zip Code
								<input v-model.trim="zip" required type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="postal-code" class="h-12 w-full rounded-[10px] border border-[#DDD6D0] bg-white px-3.5 font-normal text-ihh-midnight-gray outline-none focus:!border-[#C9665A] focus:!shadow-[0_0_0_3px_rgba(229,151,138,0.25)] focus:!ring-0" />
							</label>
							<button type="submit" class="mt-1.5 h-[52px] rounded-[10px] bg-ihh-dark-salmon text-[17px] font-semibold text-ihh-midnight-gray transition-colors duration-150 hover:bg-[#DD8678] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-ihh-dark-salmon" :disabled="loading">
								{{ loading ? 'Looking up…' : 'Start Return' }}
							</button>
						</form>
					</aside>
					<!-- Refunds copy is hidden while RESTOCKING_RATE is 0. Restore this section when the 2% fee returns. -->
					<section class="flex flex-col gap-1.5 rounded-xl bg-[#F4EFEA] px-6 py-[22px]">
						<h2 class="text-[17px] font-semibold">Received a wrong or defective item?</h2>
						<p class="text-base leading-normal text-[#5E5863]">
							Contact us through our
							<router-link to="/help-center" class="text-[#C9665A] underline underline-offset-[3px] hover:text-[#A84E43]">help center</router-link>
							instead, and we'll make it right.
						</p>
					</section>
				</div>
			</template>

			<template v-else-if="step === 'select' || step === 'confirm'">
				<header class="mb-10 flex flex-col gap-5">
					<nav class="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium" aria-label="Return progress">
						<span v-for="stage in tracker" :key="stage.label" class="flex items-center gap-2" :class="stage.state === 'active' ? 'text-ihh-midnight-gray' : 'text-[#7A7480]'">
							<span
								class="flex h-[22px] w-[22px] items-center justify-center rounded-full text-xs font-semibold"
								:class="{
									'bg-ihh-dark-salmon text-ihh-midnight-gray': stage.state === 'active',
									'bg-[#FBE9E5] text-[#B65A4E]': stage.state === 'done',
									'bg-[#ECE7E2] text-[#7A7480]': stage.state === 'upcoming',
								}"
							>{{ stage.mark }}</span>
							{{ stage.label }}
						</span>
					</nav>
					<div class="flex flex-col gap-2">
						<div class="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
							<h1 class="text-[36px] font-semibold leading-[1.15] tracking-[-0.02em]">{{ step === 'confirm' ? 'Confirm your return' : `Order ${orderNumber}` }}</h1>
							<button v-if="step === 'select'" type="button" class="text-[15px] text-[#C9665A] underline underline-offset-[3px] hover:text-[#A84E43]" @click="reset">Different order</button>
						</div>
						<p v-if="step === 'select'" class="text-[17px] text-[#5E5863]">Select items and quantities to start your return.</p>
					</div>
				</header>
				<div class="flex gap-12" :class="step === 'select' ? 'flex-col md3:flex-row-reverse md3:flex-wrap md3:items-start' : 'flex-row-reverse flex-wrap items-start'">
					<aside class="flex min-w-0 flex-col gap-5 rounded-2xl border border-[#ECE7E2] bg-white p-7 shadow-[0_1px_2px_rgba(58,53,64,0.04),0_8px_24px_rgba(58,53,64,0.05)]" :class="step === 'select' ? 'order-2 w-full flex-none md3:order-none md3:w-auto md3:flex-[1_1_360px]' : 'flex-[1_1_360px]'">
						<div class="flex flex-col gap-1">
							<h2 class="text-lg font-semibold">Your return</h2>
							<span class="text-[15px] text-[#7A7480]">Order {{ orderNumber }}</span>
						</div>
						<p v-if="!hasSelection" class="m-0 rounded-[10px] bg-[#F4EFEA] p-4 text-[15px] leading-normal text-[#5E5863]">No items selected yet.</p>
						<template v-else>
							<div class="flex flex-col gap-2.5 border-b border-[#ECE7E2] pb-5">
								<div v-for="row in selectedRows" :key="row.selectionId" class="flex items-baseline gap-3 text-[15px] leading-snug">
									<span class="min-w-0 flex-1">{{ row.name }} <span class="text-[#7A7480]">× {{ row.qty }}</span></span>
									<span class="shrink-0 tabular-nums">{{ money(row.amountEx) }}</span>
								</div>
								<div class="flex items-baseline gap-3 text-[15px] leading-snug">
									<span class="min-w-0 flex-1">Tax</span>
									<span class="shrink-0 tabular-nums">{{ money(summaryTax) }}</span>
								</div>
							</div>
							<div class="flex justify-between text-base font-semibold">
								<span>Items plus tax</span>
								<span class="tabular-nums">{{ money(summaryTotal) }}</span>
							</div>
						</template>
						<button v-if="step === 'select'" type="button" class="h-[52px] rounded-[10px] bg-ihh-dark-salmon text-[17px] font-semibold text-ihh-midnight-gray transition-colors duration-150 hover:bg-[#DD8678] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-ihh-dark-salmon" :disabled="!hasSelection || loading" @click="requestQuote">
							Continue
						</button>
					</aside>
					<div class="flex min-w-0 flex-col gap-5" :class="step === 'select' ? 'order-1 w-full flex-none md3:order-none md3:w-auto md3:flex-[999_1_480px]' : 'flex-[999_1_480px]'">
						<div v-if="step === 'select'" class="flex w-full min-w-0 flex-col gap-2">
							<div
								v-for="line in lines"
								:key="line.selectionId"
								class="w-full min-w-0 max-w-full overflow-hidden rounded-xl border transition-[border-color,box-shadow] duration-150"
								:class="line.eligible ? (picked[line.selectionId] ? 'border-ihh-dark-salmon bg-white shadow-[0_0_0_3px_rgba(229,151,138,0.18)]' : 'border-[#ECE7E2] bg-white') : 'border-[#F4EFEA] bg-[#F4EFEA]'"
							>
								<div
									v-if="line.eligible"
									role="checkbox"
									:aria-checked="picked[line.selectionId] ? 'true' : 'false'"
									tabindex="0"
									class="flex w-full min-w-0 cursor-pointer items-start gap-4 py-4 pl-4 pr-4 sm:items-center sm:pr-5"
									@click="toggle(line)"
									@keydown.enter.prevent="toggle(line)"
									@keydown.space.prevent="toggle(line)"
								>
									<img v-if="line.image" :src="line.image" alt="" class="h-14 w-14 shrink-0 rounded-lg bg-[#F4EFEA] object-cover" />
									<div v-else class="h-14 w-14 shrink-0 rounded-lg bg-[#F4EFEA]" aria-hidden="true"></div>
									<div class="flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-1">
										<div class="flex min-w-0 items-start gap-3">
											<div class="min-w-0 flex-1">
												<span class="block break-words text-base font-medium leading-snug">{{ line.name }}</span>
												<span v-if="showKitParent(line)" class="mt-1 block text-sm text-[#7A7480]">Part of {{ line.parentName }}</span>
											</div>
											<span
												class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white sm:hidden"
												:class="picked[line.selectionId] ? 'bg-[#D28B80]' : 'border-[1.5px] border-[#D9D3CE] bg-white'"
											>
												<svg v-if="picked[line.selectionId]" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
													<path d="M3 7.5l2.5 2.5L11 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
												</svg>
											</span>
										</div>
										<div class="flex min-w-0 items-center justify-between gap-3">
											<span class="min-w-0 text-sm leading-snug text-[#7A7480]">{{ money(line.unitAmount) }} each · {{ line.shippedQty }} shipped</span>
											<div v-if="picked[line.selectionId] && line.returnableQty > 1" class="inline-flex h-9 shrink-0 items-center rounded-full border border-[#E4DED8] bg-white pl-1 pr-1 text-ihh-midnight-gray sm:hidden" @click.stop>
												<button type="button" class="flex h-9 w-8 items-center justify-center text-lg leading-none disabled:cursor-not-allowed disabled:text-[#CFC7C0]" :disabled="picked[line.selectionId].quantity <= 1" aria-label="Decrease quantity" @click="changeQty(line, -1)">−</button>
												<span class="min-w-4 text-center text-base font-semibold tabular-nums">{{ picked[line.selectionId].quantity }}</span>
												<button type="button" class="flex h-9 w-8 items-center justify-center text-lg leading-none disabled:cursor-not-allowed disabled:text-[#CFC7C0]" :disabled="picked[line.selectionId].quantity >= line.returnableQty" aria-label="Increase quantity" @click="changeQty(line, 1)">+</button>
											</div>
										</div>
									</div>
									<div v-if="picked[line.selectionId] && line.returnableQty > 1" class="hidden shrink-0 items-center gap-3 text-lg leading-none text-ihh-midnight-gray sm:flex" @click.stop>
										<button type="button" class="flex h-8 w-6 items-center justify-center disabled:cursor-not-allowed disabled:text-[#CFC7C0]" :disabled="picked[line.selectionId].quantity <= 1" aria-label="Decrease quantity" @click="changeQty(line, -1)">−</button>
										<span class="min-w-4 text-center text-base tabular-nums">{{ picked[line.selectionId].quantity }}</span>
										<button type="button" class="flex h-8 w-6 items-center justify-center disabled:cursor-not-allowed disabled:text-[#CFC7C0]" :disabled="picked[line.selectionId].quantity >= line.returnableQty" aria-label="Increase quantity" @click="changeQty(line, 1)">+</button>
									</div>
									<span
										class="hidden h-6 w-6 shrink-0 items-center justify-center rounded-md border-[1.5px] text-white sm:flex"
										:class="picked[line.selectionId] ? 'border-[#C9665A] bg-[#C9665A]' : 'border-[#CFC7C0] bg-white'"
									>
										<svg v-if="picked[line.selectionId]" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
											<path d="M3 7.5l2.5 2.5L11 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
										</svg>
									</span>
								</div>
								<div v-else class="flex w-full min-w-0 items-start gap-4 py-4 pl-4 pr-4 sm:items-center sm:pr-5">
									<img v-if="line.image" :src="line.image" alt="" class="h-14 w-14 shrink-0 rounded-lg bg-[#EAE3DC] object-cover" />
									<div v-else class="h-14 w-14 shrink-0 rounded-lg bg-[#EAE3DC]" aria-hidden="true"></div>
									<div class="flex min-w-0 flex-1 flex-col gap-2 sm:gap-1">
										<span class="block break-words text-base font-medium leading-snug text-ihh-midnight-gray sm:text-[#5E5863]">{{ line.name }}</span>
										<span v-if="showKitParent(line)" class="text-sm text-[#7A7480]">Part of {{ line.parentName }}</span>
										<span class="hidden text-sm text-[#7A7480] sm:block">{{ money(line.unitAmount) }} each · {{ line.shippedQty }} shipped</span>
										<span class="inline-block max-w-full self-start rounded-full bg-white px-3.5 py-1.5 text-sm font-medium leading-snug text-[#5E5863] sm:hidden">{{ line.reason || 'Item is ineligible for return' }}</span>
									</div>
									<span class="hidden shrink-0 rounded-full bg-white px-2.5 py-1 text-[13px] font-medium text-[#5E5863] sm:inline">{{ line.reason || 'Item is ineligible for return' }}</span>
								</div>
							</div>
						</div>
						<div v-else class="flex max-w-[640px] flex-col gap-5">
							<p v-if="labelQuote.repriced" class="text-[15px] text-[#5E5863]">The label price changed. Please confirm the new amount.</p>
							<div class="overflow-hidden rounded-xl border border-[#ECE7E2] bg-white">
								<div class="flex flex-col gap-3 px-6 py-5 text-base tabular-nums">
									<div class="flex justify-between gap-4"><span class="text-[#5E5863]">Items plus tax</span><span>{{ money(labelQuote.itemTotal) }}</span></div>
									<div v-if="Number(labelQuote.restockingFee) > 0" class="flex justify-between gap-4"><span class="text-[#5E5863]">Restocking fee (2%)</span><span>−{{ money(labelQuote.restockingFee) }}</span></div>
									<div v-if="!labelQuote.ownLabel" class="flex justify-between gap-4"><span class="text-[#5E5863]">UPS Ground label</span><span>−{{ money(labelQuote.labelCost) }}</span></div>
								</div>
								<div class="flex items-baseline justify-between gap-4 bg-[#FBF0EE] px-6 py-[18px] tabular-nums">
									<span class="text-base font-semibold">Estimated refund</span>
									<span class="text-2xl font-semibold">{{ money(labelQuote.estimatedRefund) }}</span>
								</div>
							</div>
							<div class="flex flex-col gap-3">
								<button v-if="!labelQuote.ownLabel" type="button" class="h-[52px] rounded-[10px] bg-ihh-dark-salmon text-[17px] font-semibold text-ihh-midnight-gray transition-colors duration-150 hover:bg-[#DD8678] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-ihh-dark-salmon" :disabled="loading" @click="accept(false)">
									{{ submitting === 'label' ? 'Creating the label…' : 'Accept and get label' }}
								</button>
								<button type="button" class="h-[52px] rounded-[10px] text-[17px] font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50" :class="labelQuote.ownLabel ? 'bg-ihh-dark-salmon text-ihh-midnight-gray hover:bg-[#DD8678] disabled:hover:bg-ihh-dark-salmon' : 'border border-[#DDD6D0] bg-white text-ihh-midnight-gray hover:bg-[#F4EFEA]'" :disabled="loading" @click="accept(true)">
									{{ submitting === 'own' ? 'Creating the return…' : 'Ship with my own label' }}
								</button>
							</div>
							<button type="button" class="self-start text-[15px] text-[#C9665A] underline underline-offset-[3px] hover:text-[#A84E43] disabled:cursor-not-allowed disabled:opacity-50" :disabled="loading" @click="decline">Back</button>
						</div>
					</div>
				</div>
			</template>

			<div v-else-if="result" class="mx-auto w-full max-w-[720px]">
				<nav class="mb-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium" aria-label="Return progress">
					<span v-for="stage in tracker" :key="stage.label" class="flex items-center gap-2" :class="stage.state === 'active' ? 'text-ihh-midnight-gray' : 'text-[#7A7480]'">
						<span
							class="flex h-[22px] w-[22px] items-center justify-center rounded-full text-xs font-semibold"
							:class="{
								'bg-ihh-dark-salmon text-ihh-midnight-gray': stage.state === 'active',
								'bg-[#FBE9E5] text-[#B65A4E]': stage.state === 'done',
								'bg-[#ECE7E2] text-[#7A7480]': stage.state === 'upcoming',
							}"
						>{{ stage.mark }}</span>
						{{ stage.label }}
					</span>
				</nav>
				<ReturnConfirmation :result="result" @restart="restart" />
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import ReturnConfirmation from '../returns/return-confirmation.vue'

export default {
	name: 'ReturnsPortal',
	components: { ReturnConfirmation },
	inject: ['store'],
	data() {
		return {
			step: 'lookup',
			orderNumber: '',
			zip: '',
			lines: [],
			recent: [],
			recentLoading: false,
			picked: {},
			labelQuote: null,
			result: null,
			loading: false,
			submitting: null,
			policyRules: [
				'You can return your order within 90 days of the order date.',
				'All returned items must be in new condition.',
				'Some items are not eligible to be returned.',
				'Original shipping costs are non-refundable.',
			],
		}
	},
	computed: {
		hasSelection() {
			return this.selectedRows.length > 0
		},
		selectedRows() {
			return this.lines
				.filter((line) => this.picked[line.selectionId])
				.map((line) => {
					const qty = Number(this.picked[line.selectionId]?.quantity) || 1
					const amount = this.roundMoney((Number(line.unitAmount) || 0) * qty)
					let tax = this.roundMoney((Number(line.unitTax) || 0) * qty)
					if (tax < 0) tax = 0
					if (tax > amount) tax = amount
					return {
						selectionId: line.selectionId,
						name: line.name,
						qty,
						amount,
						tax,
						amountEx: this.roundMoney(amount - tax),
					}
				})
		},
		summaryTax() {
			return this.roundMoney(this.selectedRows.reduce((sum, row) => sum + row.tax, 0))
		},
		summaryTotal() {
			return this.roundMoney(this.selectedRows.reduce((sum, row) => sum + row.amountEx, 0) + this.summaryTax)
		},
		tracker() {
			const current = this.step === 'done' ? 2 : this.step === 'confirm' ? 1 : 0
			const shippingLabel = this.result?.ownLabel || this.labelQuote?.ownLabel ? 'Ship return' : 'Get label'
			return ['Select items', 'Confirm', shippingLabel].map((label, index) => ({
				label,
				mark: index < current ? '✓' : String(index + 1),
				state: index < current ? 'done' : index === current ? 'active' : 'upcoming',
			}))
		},
	},
	mounted() {
		this.loadRecent()
	},
	watch: {
		step() {
			this.scrollToComponent()
		},
		'store.user.id'() {
			this.loadRecent()
		},
	},
	methods: {
		scrollToComponent() {
			this.$nextTick(() => {
				const el = this.$el
				if (!el?.getBoundingClientRect) return
				const header = document.querySelector('.fixed.top-0.z-30')
				const offset = header ? header.getBoundingClientRect().height : 0
				const top = el.getBoundingClientRect().top + window.scrollY - offset
				window.scrollTo({ top: Math.max(0, top) })
			})
		},
		money(value) {
			return `$${Number(value || 0).toFixed(2)}`
		},
		roundMoney(value) {
			const n = Number(value)
			if (!Number.isFinite(n)) return 0
			return Math.round(n * 100) / 100
		},
		orderDate(value) {
			const date = new Date(value)
			if (Number.isNaN(date.getTime())) return ''
			return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
		},
		showKitParent(line) {
			return !!(line?.component && line.parentName && line.parentName !== line.name)
		},
		orderMeta(order) {
			const date = this.orderDate(order.dateCreated)
			const count = Number(order.itemCount)
			if (!Number.isFinite(count) || count < 1) return date
			const items = count === 1 ? '1 item' : `${count} items`
			return date ? `${date} · ${items}` : items
		},
		changeQty(line, delta) {
			const current = this.picked[line.selectionId]
			if (!current) return
			const next = current.quantity + delta
			if (next < 1 || next > line.returnableQty) return
			this.picked = {
				...this.picked,
				[line.selectionId]: { quantity: next },
			}
		},
		reset() {
			this.step = 'lookup'
			this.lines = []
			this.picked = {}
			this.labelQuote = null
			this.result = null
		},
		restart() {
			this.orderNumber = ''
			this.zip = ''
			this.reset()
		},
		escapeHtml(value) {
			return String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))
		},
		showError(message, details, title) {
			const lines = (Array.isArray(details) ? details : []).filter((line) => typeof line === 'string' && line)
			if (!lines.length) {
				return Swal.fire({ icon: 'error', title, text: message })
			}
			return Swal.fire({
				icon: 'error',
				title: message,
				html: `<ul style="text-align:left;margin:0;padding-left:1.25rem">${lines.map((line) => `<li>${this.escapeHtml(line)}</li>`).join('')}</ul>`,
			})
		},
		alertFrom(err, fallback) {
			const data = err?.response?.data
			const message = typeof data?.message === 'string' && data.message ? data.message : fallback
			return this.showError(message, data?.details)
		},
		authHeaders() {
			const token = window.localStorage.getItem('token')
			return token ? { Authorization: token } : {}
		},
		async loadRecent() {
			if (!this.store?.user?.id) {
				this.recent = []
				this.recentLoading = false
				return
			}
			this.recentLoading = true
			try {
				const { data } = await axios.get(`${this.store.api}/controllers/returns/recent`, { headers: this.authHeaders() })
				this.recent = data?.orders || []
			} catch {
				this.recent = []
			} finally {
				this.recentLoading = false
			}
		},
		selectRecent(order) {
			this.orderNumber = String(order.orderNumber)
			this.zip = ''
			return this.lookup()
		},
		async lookup() {
			this.loading = true
			try {
				const { data } = await axios.post(
					`${this.store.api}/controllers/returns`,
					{
						orderNumber: this.orderNumber,
						zip: this.zip,
					},
					{ headers: this.authHeaders() },
				)
				if (data?.expired) {
					this.showError(data.message || 'This order is outside the 90-day return window, so it can no longer be returned online.', null, 'Outside Return Window')
					return
				}
				if (!data?.found || data.international || data.unavailable) {
					this.showError(data?.message || "We couldn't find an order with that number and zip code.", data?.details)
					return
				}
				this.lines = data.lines || []
				this.picked = {}
				this.step = 'select'
			} catch (err) {
				this.alertFrom(err, 'We could not look up this order right now. Please try again.')
			} finally {
				this.loading = false
			}
		},
		toggle(line) {
			if (!line.eligible) return
			if (this.picked[line.selectionId]) {
				delete this.picked[line.selectionId]
				this.picked = { ...this.picked }
				return
			}
			this.picked = { ...this.picked, [line.selectionId]: { quantity: 1 } }
		},
		selectedItems() {
			return Object.entries(this.picked).map(([selectionId, pick]) => ({
				selectionId,
				quantity: pick.quantity,
			}))
		},
		showCalculating() {
			return Swal.fire({
				title: 'Calculating Refund',
				allowOutsideClick: false,
				allowEscapeKey: false,
				showConfirmButton: false,
				didOpen: () => {
					Swal.showLoading()
				},
			})
		},
		async requestQuote(ownLabel = false) {
			ownLabel = ownLabel === true
			const items = this.selectedItems()
			if (!items.length) {
				this.showError('Choose at least one item to return.')
				return
			}
			this.loading = true
			this.showCalculating()
			let continueOwnLabel = false
			try {
				const { data } = await axios.post(
					`${this.store.api}/controllers/returns/quote`,
					{
						orderNumber: this.orderNumber,
						zip: this.zip,
						items,
						ownLabel: ownLabel === true,
					},
					{ headers: this.authHeaders() },
				)
				this.labelQuote = data
				this.step = 'confirm'
				Swal.close()
			} catch (err) {
				const data = err?.response?.data
				if (!ownLabel && data?.code === 'label_exceeds_refund') {
					const choice = await Swal.fire({
						icon: 'error',
						text: data.message,
						showCancelButton: true,
						confirmButtonText: 'Continue with my own label',
						cancelButtonText: 'Cancel',
						confirmButtonColor: '#e59789',
					})
					continueOwnLabel = choice.isConfirmed
				} else {
					await this.alertFrom(err, 'We could not price a return label. Please try again.')
				}
			} finally {
				this.loading = false
			}
			if (continueOwnLabel) return this.requestQuote(true)
		},
		decline() {
			this.labelQuote = null
			this.step = 'select'
		},
		async accept(ownLabel) {
			this.loading = true
			this.submitting = ownLabel ? 'own' : 'label'
			try {
				const { data } = await axios.post(
					`${this.store.api}/controllers/returns/create`,
					{
						orderNumber: this.orderNumber,
						zip: this.zip,
						quoteId: this.labelQuote.quoteId,
						ownLabel,
					},
					{ headers: this.authHeaders() },
				)
				if (data?.repriced) {
					this.labelQuote = { ...this.labelQuote, ...data }
					return
				}
				this.result = data
				this.step = 'done'
			} catch (err) {
				this.alertFrom(err, ownLabel ? 'We could not create the return. Please try again.' : 'We could not create the return label. Please try again.')
			} finally {
				this.loading = false
				this.submitting = null
			}
		},
	},
}
</script>
